import { NavLink, useLocation } from "react-router-dom";
import type { CSSProperties, ReactNode } from "react";
import { useAuth } from "../auth/AuthProvider.js";
import { visibleNavForRole } from "./nav.js";
import { BrandLogo } from "./BrandLogo.js";
import type { UserRole } from "../types.js";

/**
 * Menu lateral (Design.md §4 / §4.1): 250px, fundo `--navy`, grupos na ordem
 * normativa OPERAÇÃO / CADASTRO / ANÁLISE com visibilidade por papel (decisão
 * 5). Item ativo: `--nav-active` + barra lateral laranja; hover aplica
 * `--nav-hover`; o cabeçalho do grupo é destacado quando um submenu está ativo.
 * Subitens de CADASTRO são indentados. Rodapé: usuário logado.
 */
export function Sidebar(): ReactNode {
  const { me, logout } = useAuth();
  const location = useLocation();
  const role = me?.role ?? "OPERATOR";
  const groups = visibleNavForRole(role);

  return (
    <nav
      style={{
        width: 250,
        flex: "0 0 250px",
        background: "var(--navy)",
        color: "var(--nav-text)",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        position: "sticky",
        top: 0
      }}
    >
      <div
        style={{
          padding: "1.05rem 1.1rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem"
        }}
      >
        <BrandLogo size={40} />
        <div style={{ lineHeight: 1.15, minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "0.04em" }}>
            FICC
          </div>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: "var(--nav-text-muted)"
            }}
          >
            FBA Inbound Command Center
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0.25rem 0.6rem" }}>
        {groups.map((group) => {
          const groupActive = group.items.some((item) =>
            isItemActive(item.path, location.pathname)
          );
          const indented = group.id === "cadastro";
          return (
            <div key={group.id} style={{ marginBottom: "1rem" }}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: groupActive
                    ? "var(--nav-text)"
                    : "var(--nav-text-muted)",
                  padding: "0.4rem 0.65rem"
                }}
              >
                {group.label}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  end={item.path === "/"}
                  className="ficc-nav-item"
                  style={({ isActive }) => navItemStyle(isActive, indented)}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </div>

      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.08)",
          padding: "0.85rem 1rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem"
        }}
      >
        <span
          aria-hidden
          style={{
            width: 34,
            height: 34,
            flex: "0 0 auto",
            borderRadius: "50%",
            background: "var(--blue)",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700
          }}
        >
          {initials(me?.name ?? me?.email ?? "?")}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {me?.name ?? me?.email ?? "Usuário"}
          </div>
          <div style={{ fontSize: 11, color: "var(--nav-text-muted)" }}>
            {roleLabel(role)} · Amazon UK
          </div>
        </div>
        <button
          type="button"
          onClick={() => void logout()}
          title="Sair"
          aria-label="Sair"
          style={{
            background: "transparent",
            border: "1px solid var(--nav-text-muted)",
            color: "var(--nav-text)",
            borderRadius: 7,
            padding: "0.3rem 0.5rem",
            fontSize: 11,
            cursor: "pointer"
          }}
        >
          Sair
        </button>
      </div>
    </nav>
  );
}

/** Item ativo: match exato para "/", prefixo para os demais. */
function isItemActive(itemPath: string, pathname: string): boolean {
  if (itemPath === "/") {
    return pathname === "/";
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function navItemStyle(isActive: boolean, indented: boolean): CSSProperties {
  // Sem `background` quando inativo: deixa a regra `.ficc-nav-item:hover`
  // (styles.css) aplicar `--nav-hover` — estilo inline sobreporia o hover.
  const base: CSSProperties = {
    display: "block",
    padding: "0.55rem 0.65rem",
    paddingLeft: indented ? "1.25rem" : "0.65rem",
    margin: "0.1rem 0",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: isActive ? 700 : 500,
    color: "var(--nav-text)",
    textDecoration: "none"
  };
  if (isActive) {
    base.background = "var(--nav-active)";
    base.boxShadow = "inset 3px 0 0 var(--orange)";
  }
  return base;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

function roleLabel(role: UserRole): string {
  return role === "ADMIN" ? "Admin" : "Operador";
}
